import { motion } from 'framer-motion';
import { Users, Target, Award, MapPin, Phone, Mail, Clock, Heart, Star, TrendingUp } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const AboutContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding-top: 80px;
`;

const Section = styled.section`
  padding: 4rem 0;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #64748b;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const StorySection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  margin-bottom: 4rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1.5rem;
    text-align: center;
  }
  
  .story-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
    
    @media (max-width: 968px) {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    
    .story-text {
      p {
        color: #64748b;
        line-height: 1.8;
        margin-bottom: 1rem;
      }
      
      .highlight {
        color: #3b82f6;
        font-weight: 600;
      }
    }
    
    .story-image {
      background: linear-gradient(135deg, #3b82f6, #2563eb);
      border-radius: 1rem;
      padding: 3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      text-align: center;
      
      .icon {
        font-size: 4rem;
        margin-bottom: 1rem;
      }
    }
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const ValueCard = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  }
  
  .value-icon {
    width: 4rem;
    height: 4rem;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin-bottom: 1.5rem;
  }
  
  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 1rem;
  }
  
  p {
    color: #64748b;
    line-height: 1.6;
  }
`;

const StatsSection = styled.div`
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  border-radius: 1rem;
  padding: 3rem;
  color: white;
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 2rem;
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 2rem;
    
    .stat-card {
      .stat-number {
        font-size: 2.5rem;
        font-weight: 800;
        margin-bottom: 0.5rem;
      }
      
      .stat-label {
        opacity: 0.9;
        font-weight: 500;
      }
    }
  }
`;

const TeamSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  margin-bottom: 4rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 2rem;
    text-align: center;
  }
  
  .team-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    
    .team-member {
      text-align: center;
      
      .member-avatar {
        width: 8rem;
        height: 8rem;
        background: linear-gradient(135deg, #3b82f6, #2563eb);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        margin: 0 auto 1rem;
        font-size: 2rem;
      }
      
      h4 {
        font-size: 1.25rem;
        font-weight: 600;
        color: #1a202c;
        margin-bottom: 0.5rem;
      }
      
      p {
        color: #64748b;
        margin-bottom: 0.5rem;
      }
      
      .member-role {
        color: #3b82f6;
        font-weight: 500;
        font-size: 0.875rem;
      }
    }
  }
`;

const ContactSection = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 3rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  h2 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a202c;
    margin-bottom: 2rem;
    text-align: center;
  }
  
  .contact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    
    .contact-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      
      .contact-icon {
        width: 3rem;
        height: 3rem;
        background: linear-gradient(135deg, #3b82f6, #2563eb);
        border-radius: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        flex-shrink: 0;
      }
      
      .contact-info {
        h4 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1a202c;
          margin-bottom: 0.25rem;
        }
        
        p {
          color: #64748b;
        }
      }
    }
  }
`;

const AboutPage = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: <Heart size={32} />,
      title: 'Passion',
      description: 'Nous sommes passionnés par la technologie Apple et nous nous engageons à offrir les meilleurs produits et services.'
    },
    {
      icon: <Award size={32} />,
      title: 'Qualité',
      description: 'Tous nos produits sont authentiques et garantis. Nous ne faisons aucun compromis sur la qualité.'
    },
    {
      icon: <Users size={32} />,
      title: 'Service Client',
      description: 'Notre équipe est dédiée à vous offrir une expérience exceptionnelle et à répondre à tous vos besoins.'
    },
    {
      icon: <Target size={32} />,
      title: 'Innovation',
      description: 'Nous restons à la pointe de la technologie pour vous proposer les dernières innovations Apple.'
    }
  ];

  const team = [
    { name: 'Directeur Général', role: 'Leadership & Vision', initials: 'DG' },
    { name: 'Responsable Commercial', role: 'Ventes & Marketing', initials: 'RC' },
    { name: 'Responsable Technique', role: 'Support & SAV', initials: 'RT' },
    { name: 'Responsable Client', role: 'Service Client', initials: 'RCL' }
  ];

  return (
    <AboutContainer>
      <Section>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageHeader>
              <h1>À propos d'INZA STORE</h1>
              <p>Votre partenaire de confiance pour tous vos produits Apple en Côte d'Ivoire</p>
            </PageHeader>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <StorySection>
              <h2>Notre histoire</h2>
              <div className="story-content">
                <div className="story-text">
                  <p>Fondée en <span className="highlight">2020</span>, INZA STORE est née d'une passion simple : rendre la technologie Apple accessible à tous en Côte d'Ivoire.</p>
                  <p>Notre parcours a commencé avec une petite boutique à Abidjan et une vision claire : offrir des produits Apple authentiques avec un service client exceptionnel.</p>
                  <p>Aujourd'hui, nous sommes fiers d'être <span className="highlight">le leader</span> de la vente de produits Apple en Côte d'Ivoire, avec des milliers de clients satisfaits.</p>
                </div>
                <div className="story-image">
                  <div className="icon">
                    <Star size={64} />
                  </div>
                  <p>Excellence depuis 2020</p>
                </div>
              </div>
            </StorySection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <ValuesGrid>
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                >
                  <ValueCard>
                    <div className="value-icon">
                      {value.icon}
                    </div>
                    <h3>{value.title}</h3>
                    <p>{value.description}</p>
                  </ValueCard>
                </motion.div>
              ))}
            </ValuesGrid>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <StatsSection>
              <h2>Nos réalisations</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">10 000+</div>
                  <div className="stat-label">Clients satisfaits</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">15 000+</div>
                  <div className="stat-label">Produits vendus</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">98%</div>
                  <div className="stat-label">Satisfaction client</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">4 ans</div>
                  <div className="stat-label">D'expérience</div>
                </div>
              </div>
            </StatsSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <TeamSection>
              <h2>Notre équipe</h2>
              <div className="team-grid">
                {team.map((member, index) => (
                  <div key={index} className="team-member">
                    <div className="member-avatar">
                      {member.initials}
                    </div>
                    <h4>{member.name}</h4>
                    <p>{member.role}</p>
                    <div className="member-role">INZA STORE</div>
                  </div>
                ))}
              </div>
            </TeamSection>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <ContactSection>
              <h2>Contactez-nous</h2>
              <div className="contact-grid">
                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin size={20} />
                  </div>
                  <div className="contact-info">
                    <h4>Adresse</h4>
                    <p>Abidjan, Plateau</p>
                    <p>Face à la BCEAO</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone size={20} />
                  </div>
                  <div className="contact-info">
                    <h4>Téléphone</h4>
                    <p>+225 20 00 00 00</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Mail size={20} />
                  </div>
                  <div className="contact-info">
                    <h4>Email</h4>
                    <p>contact@inza-store.ci</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Clock size={20} />
                  </div>
                  <div className="contact-info">
                    <h4>Horaires</h4>
                    <p>Lun-Ven: 8h-18h</p>
                    <p>Sam: 9h-15h</p>
                  </div>
                </div>
              </div>
            </ContactSection>
          </motion.div>
        </Container>
      </Section>
    </AboutContainer>
  );
};

export default AboutPage;
